import React, { useEffect, useMemo, useState } from 'react';
import PitchMatchGraph from './PitchMatchGraph';
import LoudnessMatchGraph from './LoudnessMatchGraph';
import MaskingCurveGraph from './MaskingCurveGraph';
import {
  PITCH_FREQS, MASKING_LEVELS, PERCEPTION_BANDS,
  ENV_OPTIONS, TRANSDUCER_OPTIONS, EAR_OPTIONS, YES_NO, PERCEPTION_NATURES, RELIABILITY_OPTIONS,
  normaliseTinnitogram, setThreshold, setPitchMatch, setLoudness, setMaskingValue,
  derivePitchType, deriveLaterality, activeEarForSummary,
  buildClinicalInterpretation, buildRecommendations,
} from './tinnitogramLogic';

const TinnitogramDataEntry = ({ data, onChange, audiologistName }) => {
  const state = useMemo(() => normaliseTinnitogram(data), [data]);
  const [pitchEar, setPitchEar] = useState('right');
  const [pitchMode, setPitchMode] = useState('threshold');
  const [loudnessEar, setLoudnessEar] = useState('right');

  const setField = (k, v) => onChange({ ...state, [k]: v });
  const setNested = (k, path, val) => {
    const parent = state[k] || {};
    const next = { ...parent };
    const [h, ...rest] = path;
    if (rest.length === 0) next[h] = val;
    else { next[h] = { ...(parent[h] || {}), [rest[0]]: val }; }
    onChange({ ...state, [k]: next });
  };

  useEffect(() => {
    if (!state.clinician && audiologistName) setField('clinician', audiologistName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeEar = activeEarForSummary(state);
  const pitchMatch = state.pitch[activeEar];
  const loud = state.loudness[activeEar];

  const tinnitusType = derivePitchType(pitchMatch.match_freq);
  const laterality = deriveLaterality(state.test_ear);

  const numInput = (val, onChangeVal, testId, placeholder = '—', step = 1) => (
    <input type="number" step={step}
      value={val === undefined || val === null ? '' : val}
      onChange={(e) => onChangeVal(e.target.value === '' ? null : Number(e.target.value))}
      className="w-full px-1 py-0.5 text-center bg-transparent focus:outline-none focus:bg-yellow-50"
      data-testid={testId} placeholder={placeholder} />
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-auto p-3" data-testid="tinnitogram-data-entry">
      {/* Common fields */}
      <div className="grid grid-cols-4 gap-2 mb-3 bg-white border border-slate-200 rounded-md p-2" data-testid="tinnitogram-common-fields">
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Audiometer</span>
          <input type="text" value={state.audiometer} onChange={(e) => setField('audiometer', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" placeholder="e.g. Interacoustics AC40" data-testid="tin-audiometer" /></label>
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Calibration</span>
          <input type="text" value={state.calibration} onChange={(e) => setField('calibration', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" placeholder="Valid (…)" data-testid="tin-calibration" /></label>
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Test Environment</span>
          <select value={state.environment} onChange={(e) => setField('environment', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="tin-environment">
            {ENV_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Transducer</span>
          <select value={state.transducer} onChange={(e) => setField('transducer', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="tin-transducer">
            {TRANSDUCER_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Test Ear</span>
          <select value={state.test_ear} onChange={(e) => setField('test_ear', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="tin-test-ear">
            {EAR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Clinician</span>
          <input type="text" value={state.clinician} onChange={(e) => setField('clinician', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" placeholder="Auto-filled" data-testid="tin-clinician" /></label>
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Counseling Given</span>
          <select value={state.counseling_given} onChange={(e) => setField('counseling_given', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="tin-counseling">
            {YES_NO.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
        <label className="text-xs"><span className="block text-slate-600 font-semibold mb-0.5">Remarks</span>
          <input type="text" value={state.remarks} onChange={(e) => setField('remarks', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" placeholder="Reliable responses / notes" data-testid="tin-remarks" /></label>
      </div>

      {/* Section 1 · Pitch Match */}
      <div className="border-2 border-slate-200 rounded-md bg-white p-2 mb-3" data-testid="pitch-card">
        <h3 className="text-sm font-bold text-sky-700 uppercase">1 · Tinnitus Pitch Match</h3>
        <div className="text-[10px] text-slate-500 mb-1">Identify the frequency that best matches the patient's tinnitus pitch.</div>
        <div className="max-w-[640px] mx-auto">
          <PitchMatchGraph
            pitch={state.pitch} plotEar={pitchEar} plotMode={pitchMode}
            onSetEar={setPitchEar} onSetMode={setPitchMode}
            onSetThreshold={(ear, f, v) => onChange(setThreshold(state, ear, f, v))}
            onSetMatch={(ear, f, v) => onChange(setPitchMatch(state, ear, f, v))}
          />
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="pitch-table">
          <thead className="bg-sky-50">
            <tr>
              <th className="border border-slate-300 px-1 py-0.5">Ear</th>
              <th className="border border-slate-300 px-1 py-0.5">Pitch Match Frequency (Hz)</th>
              <th className="border border-slate-300 px-1 py-0.5">Equivalent Hearing Level (dB HL)</th>
            </tr>
          </thead>
          <tbody>
            {['right', 'left'].map((ear, i) => (
              <tr key={ear} className={i % 2 ? 'bg-slate-50' : ''}>
                <td className={`border border-slate-300 px-1 py-0.5 font-bold ${ear === 'right' ? 'text-red-700' : 'text-blue-700'}`}>{ear === 'right' ? 'Right Ear (RE)' : 'Left Ear (LE)'}</td>
                <td className="border border-slate-300 p-0">
                  {numInput(state.pitch[ear].match_freq, (v) => onChange(setPitchMatch(state, ear, v, state.pitch[ear].match_hl ?? 30)), `pitch-match-freq-${ear}`, 'e.g. 6000', 250)}
                </td>
                <td className="border border-slate-300 p-0">
                  {numInput(state.pitch[ear].match_hl, (v) => onChange(setPitchMatch(state, ear, state.pitch[ear].match_freq, v)), `pitch-match-hl-${ear}`, 'e.g. 30', 5)}
                </td>
              </tr>
            ))}
            {['right', 'left'].map((ear) => (
              <tr key={`th-${ear}`}>
                <td colSpan={3} className="border border-slate-300 px-1 py-0.5 bg-slate-50 text-[9px]">
                  <span className={`font-bold mr-1 ${ear === 'right' ? 'text-red-700' : 'text-blue-700'}`}>{ear === 'right' ? 'RE' : 'LE'} Threshold (dB HL):</span>
                  <span className="inline-flex gap-1">
                    {PITCH_FREQS.map((f) => (
                      <span key={f} className="inline-flex items-center gap-0.5">
                        <span className="text-slate-500">{f >= 1000 ? `${f / 1000}K` : f}:</span>
                        <input type="number" step="5" value={state.pitch[ear].thresholds?.[String(f)] ?? ''}
                          onChange={(e) => onChange(setThreshold(state, ear, f, e.target.value === '' ? null : Number(e.target.value)))}
                          className="w-9 px-0.5 py-0 text-[9px] border border-slate-300 rounded text-center focus:outline-none focus:bg-yellow-50"
                          data-testid={`pitch-thr-${ear}-${f}`} />
                      </span>
                    ))}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Section 2 · Loudness Match */}
      <div className="border-2 border-slate-200 rounded-md bg-white p-2 mb-3" data-testid="loudness-card">
        <h3 className="text-sm font-bold text-sky-700 uppercase">2 · Tinnitus Loudness Match</h3>
        <div className="text-[10px] text-slate-500 mb-1">Quantify tinnitus loudness and masking characteristics.</div>
        <div className="max-w-[640px] mx-auto">
          <LoudnessMatchGraph
            loudness={state.loudness} activeEar={loudnessEar}
            onSetEar={setLoudnessEar}
            onSetValue={(ear, k, v) => onChange(setLoudness(state, ear, k, v))}
          />
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="loudness-table">
          <thead className="bg-sky-50">
            <tr>
              <th className="border border-slate-300 px-1 py-0.5">Parameter</th>
              <th className="border border-slate-300 px-1 py-0.5 text-red-700">Right Ear (RE)</th>
              <th className="border border-slate-300 px-1 py-0.5 text-blue-700">Left Ear (LE)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-300 px-1 py-0.5 font-semibold">Tinnitus Pitch Match (Hz)</td>
              <td className="border border-slate-300 p-0">{numInput(state.pitch.right.match_freq, (v) => onChange(setPitchMatch(state, 'right', v, state.pitch.right.match_hl ?? 30)), 'loud-pitch-right', 'e.g. 6000', 250)}</td>
              <td className="border border-slate-300 p-0">{numInput(state.pitch.left.match_freq, (v) => onChange(setPitchMatch(state, 'left', v, state.pitch.left.match_hl ?? 30)), 'loud-pitch-left', '—', 250)}</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="border border-slate-300 px-1 py-0.5 font-semibold">Tinnitus Loudness Match (dB SL)</td>
              <td className="border border-slate-300 p-0">{numInput(state.loudness.right.loudness_db_sl, (v) => setNested('loudness', ['right', 'loudness_db_sl'], v), 'loud-loud-right', 'e.g. 12')}</td>
              <td className="border border-slate-300 p-0">{numInput(state.loudness.left.loudness_db_sl, (v) => setNested('loudness', ['left', 'loudness_db_sl'], v), 'loud-loud-left', '—')}</td>
            </tr>
            <tr>
              <td className="border border-slate-300 px-1 py-0.5 font-semibold">Minimum Masking Level (dB SL)</td>
              <td className="border border-slate-300 p-0">{numInput(state.loudness.right.mml_db_sl, (v) => setNested('loudness', ['right', 'mml_db_sl'], v), 'loud-mml-right', 'e.g. 16')}</td>
              <td className="border border-slate-300 p-0">{numInput(state.loudness.left.mml_db_sl, (v) => setNested('loudness', ['left', 'mml_db_sl'], v), 'loud-mml-left', '—')}</td>
            </tr>
            {['right', 'left'].map((ear) => (
              <tr key={`ri2-${ear}`} className={ear === 'right' ? '' : 'bg-slate-50'}>
                <td className="border border-slate-300 px-1 py-0.5 font-semibold">Residual Inhibition ({ear === 'right' ? 'RE' : 'LE'})</td>
                <td colSpan={2} className="border border-slate-300 p-0.5">
                  <div className="flex items-center gap-2 text-[10px]">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!!state.loudness[ear].ri_present} onChange={(e) => setNested('loudness', [ear, 'ri_present'], e.target.checked)} data-testid={`loud-ri-present-${ear}`} />Present</label>
                    <input type="number" min={0} step={1} disabled={!state.loudness[ear].ri_present}
                      value={state.loudness[ear].ri_minutes ?? ''} onChange={(e) => setNested('loudness', [ear, 'ri_minutes'], e.target.value === '' ? null : Number(e.target.value))}
                      className="w-16 px-1 py-0.5 border border-slate-300 rounded text-center disabled:bg-slate-100"
                      placeholder="min" data-testid={`loud-ri-minutes-${ear}`} />
                    <span className="text-slate-500">minutes</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Section 3 · Masking Curve */}
      <div className="border-2 border-slate-200 rounded-md bg-white p-2 mb-3" data-testid="masking-card">
        <h3 className="text-sm font-bold text-sky-700 uppercase">3 · Masking Curve <span className="text-[10px] normal-case font-normal text-slate-500 ml-1">(Both Ears)</span></h3>
        <div className="text-[10px] text-slate-500 mb-1">Show how tinnitus perception changes with increasing masking noise. Right Ear = red square, Left Ear = blue dot.</div>
        <div className="max-w-[640px] mx-auto">
          <div className="flex items-center gap-1 mb-1" data-testid="masking-ear-toggle">
            <span className="text-[10px] text-slate-500 font-semibold uppercase mr-1">Plot into:</span>
            {['right', 'left'].map((e) => (
              <button key={e} type="button" data-testid={`masking-ear-${e}`}
                onClick={() => setNested('masking', ['ear'], e)}
                className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${state.masking.ear === e ? 'text-white' : 'bg-slate-100 text-slate-600'}`}
                style={state.masking.ear === e ? { backgroundColor: e === 'right' ? '#dc2626' : '#2563eb' } : {}}
              >{e === 'right' ? 'RE' : 'LE'}</button>
            ))}
          </div>
          <MaskingCurveGraph
            masking={state.masking}
            activeEar={state.masking.ear}
            onSetValue={(ear, db, perc) => onChange(setMaskingValue(state, ear, db, perc))}
          />
          <table className="w-full text-[10px] border border-slate-300 mt-2" data-testid="masking-table">
            <thead className="bg-sky-50">
              <tr>
                <th className="border border-slate-300 px-1 py-0.5">Masking Noise Level (dB HL)</th>
                <th className="border border-slate-300 px-1 py-0.5 text-red-700">Right Ear (RE)</th>
                <th className="border border-slate-300 px-1 py-0.5 text-blue-700">Left Ear (LE)</th>
              </tr>
            </thead>
            <tbody>
              {MASKING_LEVELS.map((db, i) => (
                <tr key={db} className={i % 2 ? 'bg-slate-50' : ''}>
                  <td className="border border-slate-300 px-1 py-0.5 text-center font-mono">{db}</td>
                  <td className="border border-slate-300 p-0">
                    <select value={state.masking.right.values[String(db)] || ''} onChange={(e) => onChange(setMaskingValue(state, 'right', db, e.target.value))}
                      className="w-full px-1 py-0.5 text-xs bg-transparent focus:outline-none focus:bg-yellow-50" data-testid={`masking-row-right-${db}`}>
                      <option value="">—</option>
                      {PERCEPTION_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </td>
                  <td className="border border-slate-300 p-0">
                    <select value={state.masking.left.values[String(db)] || ''} onChange={(e) => onChange(setMaskingValue(state, 'left', db, e.target.value))}
                      className="w-full px-1 py-0.5 text-xs bg-transparent focus:outline-none focus:bg-yellow-50" data-testid={`masking-row-left-${db}`}>
                      <option value="">—</option>
                      {PERCEPTION_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary + interpretation + recommendations */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="border border-slate-200 rounded p-2 bg-white" data-testid="tin-summary">
          <div className="text-[10px] font-bold uppercase text-slate-600 mb-1">Summary of Results</div>
          <table className="w-full text-[10px] border border-slate-300">
            <tbody>
              <tr><td className="border border-slate-300 px-1 py-0.5 font-semibold w-1/2">Tinnitus Type (based on pitch)</td><td className="border border-slate-300 px-1 py-0.5" data-testid="sum-tin-type">{tinnitusType || '—'}</td></tr>
              <tr className="bg-slate-50"><td className="border border-slate-300 px-1 py-0.5 font-semibold">Tinnitus Pitch Match</td><td className="border border-slate-300 px-1 py-0.5" data-testid="sum-pitch-match">{pitchMatch.match_freq ? `${pitchMatch.match_freq.toLocaleString()} Hz (${activeEar === 'right' ? 'Right Ear' : 'Left Ear'})` : '—'}</td></tr>
              <tr><td className="border border-slate-300 px-1 py-0.5 font-semibold">Tinnitus Loudness Match</td><td className="border border-slate-300 px-1 py-0.5" data-testid="sum-loudness">{loud.loudness_db_sl !== null && loud.loudness_db_sl !== undefined ? `${loud.loudness_db_sl} dB SL` : '—'}</td></tr>
              <tr className="bg-slate-50"><td className="border border-slate-300 px-1 py-0.5 font-semibold">Minimum Masking Level (MML)</td><td className="border border-slate-300 px-1 py-0.5" data-testid="sum-mml">{loud.mml_db_sl !== null && loud.mml_db_sl !== undefined ? `${loud.mml_db_sl} dB SL` : '—'}</td></tr>
              <tr><td className="border border-slate-300 px-1 py-0.5 font-semibold">Residual Inhibition (RI)</td><td className="border border-slate-300 px-1 py-0.5" data-testid="sum-ri">{loud.ri_present ? `Present${loud.ri_minutes ? ` (${loud.ri_minutes} minutes)` : ''}` : 'Absent'}</td></tr>
              <tr className="bg-slate-50"><td className="border border-slate-300 px-1 py-0.5 font-semibold">Tinnitus Laterality</td><td className="border border-slate-300 px-1 py-0.5" data-testid="sum-lat">{laterality || '—'}</td></tr>
              <tr><td className="border border-slate-300 px-1 py-0.5 font-semibold">Tinnitus Perception Nature</td>
                <td className="border border-slate-300 p-0">
                  <select value={state.summary.perception_nature} onChange={(e) => setNested('summary', ['perception_nature'], e.target.value)} className="w-full px-1 py-0.5 text-[10px] bg-transparent" data-testid="sum-nature">
                    {PERCEPTION_NATURES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
              </tr>
              <tr className="bg-slate-50"><td className="border border-slate-300 px-1 py-0.5 font-semibold">Test Reliability</td>
                <td className="border border-slate-300 p-0">
                  <select value={state.summary.test_reliability} onChange={(e) => setNested('summary', ['test_reliability'], e.target.value)} className="w-full px-1 py-0.5 text-[10px] bg-transparent" data-testid="sum-reliability">
                    {RELIABILITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="grid grid-rows-2 gap-2">
          <div className="border border-slate-200 rounded p-2 bg-white">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold uppercase text-slate-600">Clinical Interpretation</div>
              <button type="button" onClick={() => setField('clinical_interpretation', buildClinicalInterpretation(state))}
                className="text-[9px] font-bold px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded hover:bg-sky-200" data-testid="tin-auto-fill-clin">Auto-fill</button>
            </div>
            <textarea value={state.clinical_interpretation} onChange={(e) => setField('clinical_interpretation', e.target.value)} rows={4}
              placeholder={"Tinnitus pitch matched at … Hz with loudness of … dB SL in the … ear.\nMinimum masking level at … dB SL.\nResidual inhibition observed for … minutes.\nFindings are consistent with subjective … tinnitus."}
              className="w-full text-[10px] border border-slate-300 rounded px-1 py-0.5 resize-y" data-testid="tin-clinical-interpretation" />
          </div>
          <div className="border border-slate-200 rounded p-2 bg-white">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold uppercase text-slate-600">Recommendations</div>
              <button type="button" onClick={() => setField('recommendations', buildRecommendations())}
                className="text-[9px] font-bold px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded hover:bg-sky-200" data-testid="tin-auto-fill-rec">Auto-fill</button>
            </div>
            <textarea value={state.recommendations} onChange={(e) => setField('recommendations', e.target.value)} rows={4}
              placeholder={"• Provide tinnitus counselling and education.\n• Consider sound therapy / tinnitus retraining strategies.\n• Monitor periodically for any change in tinnitus characteristics.\n• Correlate with pure tone audiometry and clinical history."}
              className="w-full text-[10px] border border-slate-300 rounded px-1 py-0.5 resize-y" data-testid="tin-recommendations" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TinnitogramDataEntry;
